import React from 'react';
import { SaveBar as AppBridgeSaveBar } from '@shopify/app-bridge-react';
import { useI18n } from '../i18n';

// 嵌入判定只在页面加载时做一次:React Router 客户端跳转(pushState)会把
// URL 上的 ?shop=&host= 洗掉,而 App Bridge 会话仍在(window.shopify),
// 渲染时再实时读 location.search 会误判成本地模式,把降级栏渲染进 iframe
// (而不是接管 Shopify admin 最顶部)。模块加载时机与 App 的鉴权分支一致,
// 两者对同一次页面加载永远得出相同结论;window.shopify.config.host 是
// App Bridge CDN 建立会话后写入的,作为参数缺失时的兜底信号。
function detectEmbedded() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('shop') && params.get('host')) return true;
  return Boolean(window.shopify && window.shopify.config && window.shopify.config.host);
}
const EMBEDDED = detectEmbedded();

// 顶部固定保存栏(即 Shopify admin 原生的 "Unsaved changes / Discard / Save"):
//   嵌入模式 → App Bridge 的 <ui-save-bar>,渲染在 Shopify admin 最顶部
//   (iframe 之外),不随页面滚动 —— 表单再长也不必滚回顶部找保存键;
//   本地开发(无 Shopify 宿主)→ 页面内的粘性深色条,观感一致,便于调试。
// visible 由页面的"脏状态"驱动:出现 = 有未保存改动;保存或放弃后置回
// false,栏自动收起。页面不再需要 Polaris Page 的 primaryAction 保存按钮。
export default function SaveBar({
  visible,
  saving = false,
  saveLabel,
  message,
  onSave,
  onDiscard
}) {
  const { t } = useI18n();
  const label = saveLabel ?? t('common.save');
  const unsavedMessage = message ?? t('savebar.unsavedChanges');
  if (EMBEDDED) {
    // variant="primary" 的按钮是 Save,普通按钮是 Discard(App Bridge 约定)。
    return (
      <AppBridgeSaveBar open={Boolean(visible)}>
        <button variant="primary" loading={saving} onClick={onSave}>
          {label}
        </button>
        <button onClick={onDiscard}>{t('savebar.discard')}</button>
      </AppBridgeSaveBar>
    );
  }

  if (!visible) return null;
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 16,
        padding: '10px 16px',
        background: '#1a1a1a',
        borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)'
      }}
    >
      <span style={{ color: '#e3e3e3', fontSize: 13 }}>{unsavedMessage}</span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onDiscard}
          disabled={saving}
          style={{
            padding: '6px 14px',
            borderRadius: 8,
            border: '1px solid #616161',
            background: 'transparent',
            color: '#e3e3e3',
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.5 : 1
          }}
        >
          {t('savebar.discard')}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          aria-label={saving ? t('savebar.saving') : undefined}
          style={{
            minWidth: 74,
            minHeight: 31,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '6px 14px',
            borderRadius: 8,
            border: 'none',
            background: '#ffffff',
            color: '#1a1a1a',
            fontWeight: 600,
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.85 : 1
          }}
        >
          {/* 保存中按钮换成转圈,和 admin 原生顶栏的加载动作一致 */}
          {saving ? (
            <>
              <style>{'@keyframes savebarSpin{to{transform:rotate(360deg)}}'}</style>
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid rgba(26, 26, 26, 0.25)',
                  borderTopColor: '#1a1a1a',
                  animation: 'savebarSpin 0.6s linear infinite'
                }}
              />
            </>
          ) : (
            label
          )}
        </button>
      </span>
    </div>
  );
}
